import * as React from "react";
import { useGetIdentity, useTranslate } from "@refinedev/core";
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { CourierImageUpload } from "../../components/courier/image-upload";
import { getlostFirebaseAuth, getInvisibleRecaptcha } from "../../utils/auth/firebaseConfig";
import { updateEmail, sendEmailVerification } from "@firebase/auth";
import { parseAndValidatePhone } from "../../utils/phone";
import RegionalDetection from "../../utils/regional-detection";
import { apiJson, apiVersion } from "../../utils/api";
import { uploadImage, getPhotoUrl } from "../../utils/upload-image";
import type { IIdentity, IProfileV1 } from "../../interfaces";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

export const UserProfile: React.FC = () => {
  const t = useTranslate();
  const { data: user, refetch: refetchIdentity } = useGetIdentity<IIdentity | null>();
  const [isEditing, setIsEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  // Form state
  const [formData, setFormData] = React.useState({
    name: user?.name || "",
    bio: user?.bio || "",
    location: user?.location || "",
    website: user?.website || "",
  });

  const [photoFile, setPhotoFile] = React.useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = React.useState<string>(user?.photo?.url || "");
  const [photoLoading, setPhotoLoading] = React.useState(false);

  // Phone change state
  const [phoneDialogOpen, setPhoneDialogOpen] = React.useState(false);
  const [phoneStage, setPhoneStage] = React.useState<"phone" | "code">("phone");
  const [newPhone, setNewPhone] = React.useState("");
  const [phoneError, setPhoneError] = React.useState<string | undefined>();
  const [phoneCode, setPhoneCode] = React.useState("");
  const [phoneCodeError, setPhoneCodeError] = React.useState<string | undefined>();
  const [phoneLoading, setPhoneLoading] = React.useState(false);
  const [confirmationResult, setConfirmationResult] = React.useState<any>(null);
  const [defaultCountry, setDefaultCountry] = React.useState<string | undefined>();
  const [currentPhone, setCurrentPhone] = React.useState<string | null>(
    getlostFirebaseAuth.auth.currentUser?.phoneNumber || null
  );

  // Email change state
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState<string | undefined>();
  const [emailLoading, setEmailLoading] = React.useState(false);
  const [currentEmail, setCurrentEmail] = React.useState<string | null>(
    getlostFirebaseAuth.auth.currentUser?.email || null
  );

  // Firebase providers
  const [providers, setProviders] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        bio: user.bio || "",
        location: user.location || "",
        website: user.website || "",
      });
      setPhotoPreview(user.photo?.url || "");
    }

    // Get Firebase auth providers
    const currentUser = getlostFirebaseAuth.auth.currentUser;
    if (currentUser) {
      const providerIds = (currentUser.providerData || [])
        .map((p) => p?.providerId)
        .filter((p) => p && p !== "password") // Filter out password provider
        .filter(Boolean);
      setProviders(providerIds);
      setCurrentPhone(currentUser.phoneNumber || null);
      setCurrentEmail(currentUser.email || null);
    }
  }, [user]);

  React.useEffect(() => {
    let mounted = true;
    RegionalDetection.detectUserRegion()
      .then((res: any) => {
        if (mounted && res?.countryCode) setDefaultCountry(res.countryCode);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setPhotoFile(selectedFile);
      setPhotoPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      let photoUrl = user?.photo?.url || "";

      // Upload photo if changed
      if (photoFile) {
        setPhotoLoading(true);
        try {
          const data = await uploadImage(photoFile, "thumb");
          photoUrl = getPhotoUrl(data);
        } catch (err: any) {
          setError(err?.message || t("profile.photo.uploadError"));
          setSaving(false);
          setPhotoLoading(false);
          return;
        } finally {
          setPhotoLoading(false);
        }
      }

      // Update profile via API
      const updatePayload: any = {
        name: formData.name,
        bio: formData.bio,
        location: formData.location,
        website: formData.website,
      };

      if (photoUrl) {
        updatePayload.photo = {
          url: photoUrl,
        };
      }
      
      await apiJson(
        `admin/${apiVersion}/me`,
        {
          method: "PUT",
          body: JSON.stringify(updatePayload),
        }
      );

      setSuccess(t("profile.saveSuccess") || "Profile updated successfully");
      setIsEditing(false);
      setPhotoFile(null);
      setFormData(prev => ({
        ...prev,
        website: updatePayload.website,
        bio: updatePayload.bio,
        location: updatePayload.location,
        name: updatePayload.name,
      }));
      if (photoUrl) {
        setPhotoPreview(photoUrl);
      }

      // Refresh header identity without reload
      try {
        await refetchIdentity();
      } catch {}
    } catch (err: any) {
      setError(err?.message || t("profile.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handlePhoneSendCode = async () => {
    setPhoneError(undefined);
    setPhoneLoading(true);
    try {
      const parsed = await parseAndValidatePhone(newPhone, defaultCountry);
      if (!parsed.e164) {
        // Use structured error message if available
        const errorMessage = parsed.error 
          ? t(parsed.error, t("auth.phone.invalid")) 
          : t("auth.phone.invalid");
        setPhoneError(errorMessage);
        setPhoneLoading(false);
        return;
      }

      const verifier = getInvisibleRecaptcha("phone-recaptcha-container");
      const cr = await getlostFirebaseAuth.updatePhoneNumber(parsed.e164, verifier);
      setConfirmationResult(cr);
      setPhoneStage("code");
    } catch (e: any) {
      setPhoneError(e?.message || t("auth.phone.failed"));
    } finally {
      setPhoneLoading(false);
    }
  };

  const handlePhoneConfirmCode = async () => {
    setPhoneCodeError(undefined);
    setPhoneLoading(true);
    try {
      await getlostFirebaseAuth.confirmPhoneLinking(confirmationResult, phoneCode);
      
      // Reload the Firebase user to get updated phone number
      await getlostFirebaseAuth.auth.currentUser?.reload();
      
      setSuccess(t("profile.phoneChangeSuccess") || "Phone number updated successfully");
      setPhoneDialogOpen(false);
      setPhoneStage("phone");
      setNewPhone("");
      setPhoneCode("");
      setConfirmationResult(null);

      // Refresh providers list and local phone state
      const currentUser = getlostFirebaseAuth.auth.currentUser;
      if (currentUser) {
        const providerIds = (currentUser.providerData || [])
          .map((p) => p?.providerId)
          .filter((p) => p);
        setProviders(providerIds);
        setCurrentPhone(currentUser.phoneNumber || null);
      }

    } catch (e: any) {
      setPhoneCodeError(e?.message || t("auth.code.invalid"));
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleClosePhoneDialog = () => {
    setPhoneDialogOpen(false);
    setPhoneStage("phone");
    setNewPhone("");
    setPhoneCode("");
    setPhoneError(undefined);
    setPhoneCodeError(undefined);
    setConfirmationResult(null);
  };

  const handleEmailSendVerification = async () => {
    setEmailError(undefined);
    setEmailLoading(true);
    try {
      if (!newEmail || !newEmail.includes("@")) {
        setEmailError(t("auth.email.invalid") || "Please enter a valid email");
        setEmailLoading(false);
        return;
      }

      const user = getlostFirebaseAuth.auth.currentUser;
      if (!user) throw new Error("No authenticated user");

      // Update email and send verification
      await updateEmail(user, newEmail);
      await sendEmailVerification(user);
      
      setSuccess(t("profile.emailChangeSuccess") || "Verification email sent. Please check your new email address to confirm the change.");
      setEmailDialogOpen(false);
      setNewEmail("");
    } catch (e: any) {
      setEmailError(e?.message || t("auth.email.failed") || "Failed to update email");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailConfirmCode = async () => {
    // This is not needed anymore since updateEmail + sendEmailVerification handles it
    handleCloseEmailDialog();
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setNewEmail("");
    setEmailError(undefined);
  };

  const getProviderIcon = (providerId: string) => {
    switch (providerId) {
      case "google.com":
        return <GoogleIcon sx={{ mr: 1 }} />;
      case "apple.com":
        return <AppleIcon sx={{ mr: 1 }} />;
      case "phone":
        return <PhoneIcon sx={{ mr: 1 }} />;
      case "password":
        return <EmailIcon sx={{ mr: 1 }} />;
      default:
        return null;
    }
  };

  const getProviderName = (providerId: string) => {
    switch (providerId) {
      case "google.com":
        return "Google";
      case "apple.com":
        return "Apple";
      case "phone":
        return "Phone";
      case "password":
        return "Email";
      default:
        return providerId;
    }
  };

  // Check if the current email is from an OAuth provider (Google or Apple)
  const isEmailFromOAuthProvider = () => {
    const currentUser = getlostFirebaseAuth.auth.currentUser;
    if (!currentUser) return false;
    
    // Check if Google or Apple provider exists
    const hasOAuthProvider = (currentUser.providerData || []).some(
      (p) => p?.providerId === "google.com" || p?.providerId === "apple.com"
    );
    
    return hasOAuthProvider;
  };

  if (!user) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {t("profile.title") || "My Profile"}
        </Typography>
        {!isEditing && (
          <Button
            startIcon={<EditIcon />}
            onClick={() => setIsEditing(true)}
            variant="outlined"
          >
            {t("profile.edit") || "Edit"}
          </Button>
        )}
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Profile Info Card */}
      <Box
        sx={{
          p: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          mb: 3,
        }}
      >
        {/* Avatar Section */}
        <Stack direction="column" alignItems="center" mb={3}>
          {isEditing ? (
            <CourierImageUpload
              previewURL={photoPreview}
              showOverlay={true}
              inputProps={{
                onChange: handlePhotoChange,
                disabled: photoLoading,
              }}
            />
          ) : (
            <Avatar
              src={photoPreview}
              alt={user.name}
              sx={{ width: 72, height: 72 }}
            />
          )}
          {photoLoading && (
            <CircularProgress
              size={24}
              sx={{ position: "absolute", mt: 9 }}
            />
          )}
        </Stack>

        {/* Form Fields */}
        <Stack spacing={2}>
          {/* Name */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {t("profile.name") || "Name"}
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
                disabled={saving}
              />
            ) : (
              <Typography variant="body1">{formData.name}</Typography>
            )}
          </Box>

          {/* Username */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {t("profile.username") || "Username"}
            </Typography>
            <Typography variant="body1">@{user.id}</Typography>
          </Box>

          {/* Bio */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {t("profile.bio") || "Bio"}
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={formData.bio}
                onChange={(e) => handleFormChange("bio", e.target.value)}
                disabled={saving}
              />
            ) : (
              <Typography variant="body1">{formData.bio || "-"}</Typography>
            )}
          </Box>

          {/* Location */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {t("profile.location") || "Location"}
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={formData.location}
                onChange={(e) => handleFormChange("location", e.target.value)}
                disabled={saving}
              />
            ) : (
              <Typography variant="body1">{formData.location || "-"}</Typography>
            )}
          </Box>

          {/* Website */}
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              {t("profile.website") || "Website"}
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                value={formData.website}
                onChange={(e) => handleFormChange("website", e.target.value)}
                disabled={saving}
              />
            ) : (
              <Typography variant="body1">
                {formData.website ? (
                  <a href={formData.website} target="_blank" rel="noopener noreferrer" style={{
                    color: 'inherit',
                    textDecoration: 'none',
                    borderBottom: '1px solid currentColor',
                    cursor: 'pointer',
                  }}>
                    {formData.website}
                  </a>
                ) : (
                  "-"
                )}
              </Typography>
            )}
          </Box>

          {/* Edit Actions */}
          {isEditing && (
            <Stack direction="row" spacing={2} mt={2}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveProfile}
                disabled={saving || photoLoading}
                fullWidth
              >
                {saving ? t("profile.saving") || "Saving..." : t("profile.save") || "Save"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    name: user?.name || "",
                    bio: user?.bio || "",
                    location: user?.location || "",
                    website: user?.website || "",
                  });
                  setPhotoPreview(user?.photo?.url || "");
                  setPhotoFile(null);
                }}
                disabled={saving}
                fullWidth
              >
                {t("profile.cancel") || "Cancel"}
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Phone Number Management */}
      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t("profile.phoneManagement") || "Phone Number"}
        </Typography>
        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            mb: 3,
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {t("profile.currentPhone") || "Current Phone Number"}
              </Typography>
              <Typography variant="body1">
                {currentPhone || "Not set"}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => setPhoneDialogOpen(true)}
              fullWidth
            >
              {t("profile.changePhone") || "Change Phone Number"}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Email Management */}
      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t("profile.emailManagement") || "Email Address"}
        </Typography>
        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            mb: 3,
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                {t("profile.currentEmail") || "Current Email Address"}
              </Typography>
              <Typography variant="body1">
                {currentEmail || "Not set"}
              </Typography>
            </Box>
            {isEmailFromOAuthProvider() && (
              <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                {t("profile.emailLinkedToProvider") || "This email is linked to your Google or Apple account and cannot be changed here."}
              </Alert>
            )}
            <Button
              variant="outlined"
              onClick={() => setEmailDialogOpen(true)}
              disabled={isEmailFromOAuthProvider()}
              fullWidth
            >
              {t("profile.changeEmail") || "Change Email Address"}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Firebase Authentication Methods */}
      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t("profile.authenticatedMethods") || "Authentication Methods"}
        </Typography>
        <Box
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Stack spacing={2}>
            {providers.length > 0 ? (
              providers.map((providerId) => (
                <Stack
                  key={providerId}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 1.5,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center">
                    {getProviderIcon(providerId)}
                    <Typography variant="body2">
                      {getProviderName(providerId)}
                    </Typography>
                  </Stack>
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={t("profile.connected") || "Connected"}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </Stack>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("profile.noMethods") || "No authentication methods connected"}
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Phone Change Dialog */}
      <Dialog
        open={phoneDialogOpen}
        onClose={handleClosePhoneDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("profile.changePhone") || "Change Phone Number"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {phoneError && (
              <Alert severity="error" onClose={() => setPhoneError(undefined)} sx={{ mb: 2 }}>
                {phoneError}
              </Alert>
            )}
            {phoneCodeError && (
              <Alert
                severity="error"
                onClose={() => setPhoneCodeError(undefined)}
                sx={{ mb: 2 }}
              >
                {phoneCodeError}
              </Alert>
            )}

            {phoneStage === "phone" ? (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t("profile.enterNewPhone") || "Enter your new phone number"}
                </Typography>
                <Box sx={{
                  '& .PhoneInput': { width: '100%', display: 'flex', alignItems: 'center', gap: '8px' },
                  '& .PhoneInputInput': {
                    width: '100%',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.23)',
                    borderRadius: '4px',
                    color: '#fff',
                    padding: '10px 12px',
                    fontSize: '0.95rem',
                    outline: 'none',
                    '::placeholder': { color: 'rgba(255,255,255,0.7)' },
                  },
                  '& .PhoneInputCountry': {
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.23)',
                    borderRadius: '4px',
                    color: '#fff',
                    padding: '6px 8px',
                    height: '40px',
                  },
                  '& .PhoneInputCountrySelect': {
                    backgroundColor: '#121212',
                    color: '#fff',
                  }
                }}>
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry={defaultCountry as any}
                    value={newPhone}
                    onChange={(val) => setNewPhone(val || "")}
                    disabled={phoneLoading}
                  />
                </Box>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t("profile.enterOTP") ||
                    "Enter the 6-digit code sent to your phone"}
                </Typography>
                <TextField
                  fullWidth
                  label={t("profile.code") || "Code"}
                  size="small"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value)}
                  disabled={phoneLoading}
                  inputProps={{ maxLength: 6 }}
                  placeholder="000000"
                />
              </Stack>
            )}
            <div id="phone-recaptcha-container" style={{ height: 0 }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhoneDialog} disabled={phoneLoading}>
            {t("profile.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={
              phoneStage === "phone" ? handlePhoneSendCode : handlePhoneConfirmCode
            }
            variant="contained"
            disabled={phoneLoading}
          >
            {phoneLoading ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : phoneStage === "phone" ? (
              t("profile.sendCode") || "Send Code"
            ) : (
              t("profile.verifyCode") || "Verify Code"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Change Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("profile.changeEmail") || "Change Email Address"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {emailError && (
              <Alert severity="error" onClose={() => setEmailError(undefined)} sx={{ mb: 2 }}>
                {emailError}
              </Alert>
            )}

            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t("profile.enterNewEmail") || "Enter your new email address"}
              </Typography>
              <TextField
                fullWidth
                label={t("profile.email") || "Email"}
                type="email"
                size="small"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={emailLoading}
                placeholder="your.email@example.com"
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailLoading}>
            {t("profile.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleEmailSendVerification}
            variant="contained"
            disabled={emailLoading}
          >
            {emailLoading ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : (
              t("profile.sendVerification") || "Send Verification Email"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
